import React, { useState, useEffect } from "react";
import { Link, NavLink, useLocation } from "react-router-dom";
import { Menu, X, ArrowRight } from "lucide-react";
import { NAV } from "@/constants/testIds";
import Logo from "@/components/site/Logo";

const links = [
    { to: "/", label: "Home", testId: NAV.home, end: true },
    { to: "/about", label: "About", testId: NAV.about },
    { to: "/framework", label: "Framework", testId: NAV.framework },
    { to: "/snapshot", label: "Snapshot", testId: NAV.snapshot },
    { to: "/foundations", label: "Foundations", testId: NAV.foundations },
    { to: "/workshop", label: "Workshop", testId: "nav-workshop" },
    { to: "/resources", label: "Resources", testId: NAV.resources },
    { to: "/contact", label: "Contact", testId: NAV.contact },
];

const Navbar = () => {
    const [open, setOpen] = useState(false);
    const [scrolled, setScrolled] = useState(false);
    const location = useLocation();

    useEffect(() => {
        const onScroll = () => setScrolled(window.scrollY > 8);
        onScroll();
        window.addEventListener("scroll", onScroll, { passive: true });
        return () => window.removeEventListener("scroll", onScroll);
    }, []);

    useEffect(() => {
        setOpen(false);
    }, [location.pathname]);

    return (
        <header
            className={`sticky top-0 z-50 w-full transition-all duration-300 ${
                scrolled
                    ? "bg-white/90 backdrop-blur-md border-b border-surface-border/60"
                    : "bg-white border-b border-transparent"
            }`}
        >
            <div className="ih-container flex h-20 items-center justify-between">
                <Logo />

                <nav className="hidden xl:flex items-center gap-8">
                    {links.map((l) => (
                        <NavLink
                            key={l.to}
                            to={l.to}
                            end={l.end}
                            data-testid={l.testId}
                            className={({ isActive }) =>
                                `relative whitespace-nowrap text-[13.5px] font-raleway font-semibold tracking-wide transition-colors ${
                                    isActive
                                        ? "text-indigo_brand"
                                        : "text-indigo_brand/70 hover:text-indigo_brand"
                                }`
                            }
                        >
                            {({ isActive }) => (
                                <span className="inline-flex flex-col items-center">
                                    {l.label}
                                    <span
                                        className={`mt-1 h-[2px] transition-all duration-300 ${
                                            isActive
                                                ? "w-6 bg-green_brand"
                                                : "w-0 bg-green_brand"
                                        }`}
                                    />
                                </span>
                            )}
                        </NavLink>
                    ))}
                </nav>

                <div className="hidden xl:flex items-center gap-3">
                    <Link
                        to="/snapshot"
                        data-testid={NAV.cta}
                        className="ih-btn-primary whitespace-nowrap !py-3 !px-6"
                    >
                        Take the Snapshot
                        <ArrowRight size={16} strokeWidth={2.2} />
                    </Link>
                </div>

                <button
                    type="button"
                    onClick={() => setOpen((v) => !v)}
                    data-testid={NAV.mobileToggle}
                    className="xl:hidden inline-flex items-center justify-center w-10 h-10 rounded-md text-indigo_brand hover:bg-surface transition"
                    aria-label="Open menu"
                >
                    {open ? <X size={22} /> : <Menu size={22} />}
                </button>
            </div>

            {open && (
                <div className="xl:hidden border-t border-surface-border/60 bg-white">
                    <div className="ih-container py-6 flex flex-col gap-1">
                        {links.map((l) => (
                            <NavLink
                                key={l.to}
                                to={l.to}
                                end={l.end}
                                data-testid={`${l.testId}-mobile`}
                                className={({ isActive }) =>
                                    `font-raleway font-semibold py-3 px-2 rounded-md transition ${
                                        isActive
                                            ? "text-indigo_brand bg-surface"
                                            : "text-indigo_brand/80 hover:bg-surface"
                                    }`
                                }
                            >
                                {l.label}
                            </NavLink>
                        ))}
                        <Link
                            to="/snapshot"
                            data-testid={`${NAV.cta}-mobile`}
                            className="ih-btn-primary mt-4 justify-center"
                        >
                            Take the Snapshot
                            <ArrowRight size={16} />
                        </Link>
                    </div>
                </div>
            )}
        </header>
    );
};

export default Navbar;
